import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "../../Utilities/axios";
import { useTypewriter, Cursor } from "react-simple-typewriter";
import {
  AiOutlineArrowLeft,
  AiOutlineArrowRight as AiArrowRight,
} from "react-icons/ai";
import SpinnerPage from "./SpinnerPage";
import SearchForm from "./SearchForm";
import ImageWithFallback from "../common/ImageWithFallback";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Hero.css";

const Hero = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const timerRef = useRef(null);
  const isPaused = useRef(false);
  const sectionRef = useRef(null);

  const getHotels = async () => {
    try {
      const res = await axios.get(
        `/registrations/hotels?sourceType=showInCompanySite&hotelGroupName=Residency`,
      );
      setSlides(res.data);
    } catch (err) {
      console.error("Failed to load hotels:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getHotels();
    AOS.init({ duration: 2000 });
  }, []);

  const goTo = useCallback(
    (idx) => {
      if (idx === current) return;
      setCurrent(idx);
    },
    [current],
  );

  const nextSlide = useCallback(() => {
    if (isPaused.current) return;
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
    isPaused.current = false;
  }, [slides.length]);

  useEffect(() => {
    if (slides.length === 0) return;
    timerRef.current = setInterval(nextSlide, 6000);
    return () => clearInterval(timerRef.current);
  }, [nextSlide, slides.length]);

  const handleImageLoad = (hotelId) => {
    setLoadedImages((prev) => ({ ...prev, [hotelId]: true }));
  };

  const [text] = useTypewriter({
    words: ["For Your Luxury and Comfort Stay"],
  });

  return (
    <>
      {isLoading && <SpinnerPage />}
      <section
        className="hero-section"
        ref={sectionRef}
        onMouseEnter={() => {
          isPaused.current = true;
        }}
        onMouseLeave={() => {
          isPaused.current = false;
        }}
        onFocus={() => {
          isPaused.current = true;
        }}
        onBlur={() => {
          isPaused.current = false;
        }}
      >
        {/* ── Full-width carousel ── */}
        <div className="hero-carousel">
          {slides.map((slide, i) => (
            <div
              key={slide.hotelId || i}
              className={`hero-carousel__slide ${i === current ? "is-active" : ""}`}
            >
              {/* Skeleton placeholder */}
              {!loadedImages[slide.hotelId] && (
                <div className="hero-carousel__skeleton" />
              )}
              <ImageWithFallback
                src={slide.picture}
                hotelName={slide.hotelName}
                alt={slide.hotelName}
                className={`hero-carousel__img ${loadedImages[slide.hotelId] ? "is-loaded" : ""}`}
                loading={i === 0 ? "eager" : "lazy"}
                onLoad={() => handleImageLoad(slide.hotelId)}
              />
            </div>
          ))}
        </div>

        {/* ── Gradient overlay ── */}
        <div className="hero-overlay" />
        {/* ── Content overlay ── */}
        <div className="hero-content">
          <p className="hero-content__eyebrow">Welcome to Residency Hotels</p>
          <div className="hero-content__typewriter">
            {text}
            <Cursor cursorStyle="|" />
          </div>
          <div className="hero-content__divider">
            <span className="hero-content__divider-line"></span>
            <span className="hero-content__divider-diamond"></span>
            <span className="hero-content__divider-line"></span>
          </div>
          <div className="hero-content__search">
            <SearchForm />
          </div>
        </div>

        {/* ── Arrows ── */}
        {slides.length > 0 && (
          <div className="hero-arrows">
            <button
              className="hero-arrows__btn"
              onClick={prevSlide}
              aria-label="Previous"
            >
              <AiOutlineArrowLeft />
            </button>
            <button
              className="hero-arrows__btn"
              onClick={nextSlide}
              aria-label="Next"
            >
              <AiArrowRight />
            </button>
          </div>
        )}
      </section>
    </>
  );
};

export default Hero;
